// ============================================================================
// CHANGO V5.0 — D8 Printable Quote (browser → PDF)
// ============================================================================
// 支持三种渲染模式：中文、英文、中英对照
// 加载 /api/quotes/:id 并渲染单页打印布局
// 用户点击"打印"调用 window.print()；浏览器打印对话框可以"另存为 PDF"
// 或服务端通过 puppeteer 自动生成 PDF 并下载
//
// 角色可见性（backend 脱敏为纵深防御）：
//   - boss    : 完整信头 + 行项 + 总计 + 成本/毛利块
//   - finance : 同 boss
//   - sales   : 信头 + 行项 + 仅公开总计（无成本/毛利）
//
// Task #112 + #113: 服务端 PDF + 双语
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { HideFromSales } from '../../components/RoleGate.jsx';
import { formatRmb, formatFx } from '../../lib/marginPreview.js';
import { getLabelsByLang } from '../../lib/quoteLocale.js';

const PRINTABLE_STATUSES = ['approved', 'issued', 'closed_won', 'closed_lost'];

const STATUS_LABEL = {
  approved: '已审',
  issued: '已出具',
  closed_won: '已成交',
  closed_lost: '流失',
  draft: '草稿',
  pending: '待审'
};

export default function QuotePrintable() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, workspace } = useAuth();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);

  // 允许通过 ?lang 查询参数覆盖报价单的语言
  const langOverride = searchParams.get('lang');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get(`/api/quotes/detail/${id}`);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '加载失败'));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (err) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="alert-error">{err}</div>
        <Link to="/app/quotes" className="btn-ghost mt-4">← 返回列表</Link>
      </div>
    );
  }
  if (!detail) {
    return <div className="px-6 py-10 text-center text-slate-400">加载中…</div>;
  }

  const status = detail.lifecycle_status || detail.status;
  if (!PRINTABLE_STATUSES.includes(status)) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="alert-error">
          当前状态为 <strong>{STATUS_LABEL[status] || status}</strong>，尚不可打印。
          请先提交审批并获得批准。
        </div>
        <div className="mt-4 flex gap-2">
          <Link to={`/app/quotes/${id}`} className="btn-ghost">← 返回详情</Link>
        </div>
      </div>
    );
  }

  const totalLinesSell = (detail.lines || []).reduce(
    (sum, l) => sum + (Number(l.line_sell_rmb) || 0), 0
  );

  // 确定最终的语言模式（优先 URL 查询参数，否则用报价单记录）
  const finalLang = langOverride || detail.lang || 'zh';
  const labels = getLabelsByLang(finalLang);

  // 格式化金额（中文用 CNY locale，英文用 EN-US）
  const formatCurrency = (value) => {
    if (finalLang === 'en') {
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value) || 0);
    }
    // 中文/双语保持原来的 formatRmb 行为
    return formatRmb(value);
  };

  // 获取客户名（支持 name_en）
  const getCustomerName = () => {
    if (finalLang === 'en' && detail.customer_name_en) {
      return detail.customer_name_en;
    }
    return detail.customer_name || '—';
  };

  return (
    <div className="bg-slate-100 min-h-screen py-6 print:bg-white print:py-0">
      {/* ---- Non-printing action bar ---- */}
      <div className="mx-auto mb-4 max-w-4xl px-6 print:hidden">
        <div className="flex items-center justify-between">
          <Link to={`/app/quotes/${id}`} className="btn-ghost">← 返回详情</Link>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={() => window.print()}
            >
              打印 / 另存为 PDF
            </button>
          </div>
        </div>
      </div>

      {/* ---- Paper ---- */}
      <article
        className="mx-auto bg-white shadow-sm print:shadow-none"
        style={{ maxWidth: '210mm', minHeight: '297mm', padding: '18mm' }}
      >
        {/* Letterhead */}
        <header className="mb-6 border-b border-slate-300 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">{labels.quotation}</div>
              <h1 className="mt-1 text-2xl font-bold text-slate-800">
                {detail.code || `Q-${detail.id}`}
              </h1>
              <p className="mt-1 text-xs text-slate-500">
                {finalLang === 'en' ? 'Status' : finalLang === 'bilingual' ? '状态 / Status' : '状态'}：{STATUS_LABEL[status] || status}
                {detail.issued_at && ` · ${labels.issued_at} ${new Date(detail.issued_at).toLocaleDateString(finalLang === 'en' ? 'en-US' : 'zh-CN')}`}
              </p>
            </div>
            <div className="text-right">
              <div className="text-base font-semibold text-slate-800">{workspace?.name || '—'}</div>
              <p className="mt-1 text-xs text-slate-500">
                {labels.responsible}：{detail.owner_display_name || detail.owner_name || detail.owner_email || user?.display_name || user?.contact_name || user?.email || '—'}
              </p>
            </div>
          </div>
        </header>

        {/* Buyer info */}
        <section className="mb-6 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{labels.buyer}</div>
            <div className="mt-1 text-base font-medium text-slate-800">
              {getCustomerName()}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{labels.terms}</div>
            <div className="mt-1 text-sm text-slate-700">
              {labels.currency_label} {detail.currency || 'CNY'}
              {detail.currency !== 'CNY' && ` · ${labels.exchange_rate} ${Number(detail.fx_rate_to_rmb).toFixed(4)}`}
              · {labels.discount} {detail.discount_pct ?? 0}% · {labels.tax_rate} {detail.tax_rate_pct ?? 0}%
            </div>
          </div>
        </section>

        {/* Line items */}
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-600">
            {labels.line_items}
          </h2>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-slate-400 text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-2">{labels.number}</th>
                <th className="py-2 pr-2">SKU</th>
                <th className="py-2 pr-2">{labels.description}</th>
                <th className="py-2 pr-2 text-right">{labels.quantity}</th>
                <th className="py-2 pr-2 text-right">{labels.unit_price} {finalLang === 'en' ? 'USD' : '¥'}</th>
                <th className="py-2 pr-2 text-right">{labels.subtotal} {finalLang === 'en' ? 'USD' : '¥'}</th>
              </tr>
            </thead>
            <tbody>
              {(detail.lines || []).map((l, i) => (
                <tr key={l.idx} className="border-b border-slate-100 align-top">
                  <td className="py-1.5 pr-2 text-slate-500 tabular-nums">{i + 1}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">{l.sku}</td>
                  <td className="py-1.5 pr-2">
                    {finalLang === 'en' && l.name_en ? l.name_en : (finalLang === 'bilingual' && l.name_en ? `${l.description}\n${l.name_en}` : (l.description || '—'))}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{l.qty}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{formatCurrency(l.unit_sell_rmb)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{formatCurrency(l.line_sell_rmb)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 font-medium">
                <td colSpan={5} className="py-2 pr-2 text-right text-slate-600">{labels.line_subtotal}</td>
                <td className="py-2 pr-2 text-right tabular-nums">{formatCurrency(totalLinesSell)}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Totals */}
        <section className="mb-6">
          <div className="flex justify-end">
            <dl className="w-80 text-sm">
              <TotalRow label={labels.sales_subtotal}
                value={formatCurrency(detail.sell_subtotal_rmb)} />
              <TotalRow label={labels.discount_amount}
                value={`-${formatCurrency(detail.discount_amount_rmb)}`} />
              <TotalRow label={labels.sales_after_discount}
                value={formatCurrency(detail.sell_after_discount_rmb)} />
              <TotalRow label={`${labels.tax} (${detail.tax_rate_pct ?? 0}%)`}
                value={formatCurrency(detail.tax_amount_rmb)} />
              <TotalRow label={labels.total_due} bold
                value={formatCurrency(detail.total_revenue_rmb)} />
              {detail.currency !== 'CNY' && (
                <TotalRow label={`${labels.exchange} ${detail.currency}`}
                  value={formatFx(detail.total_revenue_fx, detail.currency)} />
              )}
            </dl>
          </div>
        </section>

        {/* Sensitive — hidden from sales users AND hidden on printed page
            unless the viewer has the right role. */}
        <HideFromSales>
          {(detail.gross_profit_rmb != null || detail.margin_pct != null) && (
            <section className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs print:border-amber-300">
              <div className="font-semibold text-amber-800">
                {labels.internal_info}
              </div>
              <div className="mt-1 grid grid-cols-3 gap-3 text-slate-700">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">{labels.cost}</div>
                  <div>{formatCurrency(detail.total_cost_rmb)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">{labels.gross_profit}</div>
                  <div>{formatCurrency(detail.gross_profit_rmb)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">{labels.margin_rate}</div>
                  <div>
                    {detail.margin_pct == null
                      ? '—'
                      : `${Number(detail.margin_pct).toFixed(2)}%`}
                  </div>
                </div>
                {Number(detail.channel_cost_rmb) > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">跨境渠道成本</div>
                    <div>{formatCurrency(detail.channel_cost_rmb)}</div>
                  </div>
                )}
                {Number(detail.entity_cost_rmb) > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">主体税费/合规成本</div>
                    <div>{formatCurrency(detail.entity_cost_rmb)}</div>
                  </div>
                )}
              </div>
            </section>
          )}
        </HideFromSales>

        {/* Footer */}
        <footer className="mt-10 border-t border-slate-300 pt-4 text-xs text-slate-500">
          <p>
            {labels.footer_note}
          </p>
          <p className="mt-1">
            {labels.legend} {detail.code || `Q-${detail.id}`} · {labels.export_time} {new Date().toLocaleString(finalLang === 'en' ? 'en-US' : 'zh-CN')}
          </p>
        </footer>
      </article>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: white !important; }
          .btn-ghost, .btn-primary { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={[
      'flex justify-between border-b border-slate-100 py-1.5',
      bold ? 'border-t-2 border-slate-400 pt-2 font-semibold text-slate-800' : 'text-slate-600'
    ].join(' ')}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
